import axios from 'axios';

function getHeaders() {
  return { "Content-Type": "application/json" };
}

const instance = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL}`,
  headers: getHeaders(),
});

const instance2 = axios.create({
  baseURL: `${import.meta.env.VITE_API_BASE_URL2}`,
  headers: getHeaders(),
});

instance.interceptors.request.use((config) => {
  if (
    [
      "/v1/user/easytogo-casino-amount-add",
      "/v1/wallet/doLoginAndLaunchEasyToCasino",
      "/v1/user/casino-amount-add",
      "/v1/wallet/doLoginAndLaunchGame",
      "/v1/wallet/login",
    ].includes(config?.url?.split("?")[0])
  ) {
    document.getElementById("loading").style.display = "flex";
  }
  const token = localStorage.getItem("token");
  if (!token) {
    return config;
  }
  config = {
    ...config,
    headers: { ...config.headers, Authorization: `Bearer ${token}` },
  };
  return config;
});

// Track whether a token refresh is already in-flight so we don't fire
// multiple refresh calls in parallel when several requests 401 at once.
let isRefreshing = false;
let refreshSubscribers = [];

const subscribeTokenRefresh = (cb) => refreshSubscribers.push(cb);
const onRefreshed = (token) => {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
};

const forceLogout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("refresh_token");
  window.location.href = "/login";
};

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    // --- Token expired: try to refresh silently ---
    if (status === 401 && !originalRequest?._retry) {
      const refreshToken = localStorage.getItem("refresh_token");

      // No refresh token stored → hard logout
      if (!refreshToken) {
        forceLogout();
        return Promise.reject(error);
      }

      // If a refresh is already in-flight, queue this request
      if (isRefreshing) {
        return new Promise((resolve) => {
          subscribeTokenRefresh((newToken) => {
            originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
            resolve(instance(originalRequest));
          });
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const { data } = await axios.post(
          `${import.meta.env.VITE_API_BASE_URL}/v1/user/refresh-token`,
          { refresh_token: refreshToken },
          { headers: { "Content-Type": "application/json" } }
        );

        if (data?.success && data?.results?.token) {
          const newToken = data.results.token;
          localStorage.setItem("token", newToken);
          if (data.results.refresh_token) {
            localStorage.setItem("refresh_token", data.results.refresh_token);
          }
          instance.defaults.headers.common["Authorization"] = `Bearer ${newToken}`;
          onRefreshed(newToken);
          originalRequest.headers["Authorization"] = `Bearer ${newToken}`;
          return instance(originalRequest);
        } else {
          // Refresh succeeded HTTP-wise but server said failure → logout
          forceLogout();
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // Refresh request itself failed (network, 401 on refresh) → logout
        forceLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // 403 → hard logout (banned / suspended, not just expired)
    if (status === 403) {
      forceLogout();
      return Promise.reject(error);
    }

    // ✅ 404 → silent
    if (status === 404) {
      return {
        data: null,
        status: 404,
        statusText: "Not Found",
        headers: {},
        config: error.config,
      };
    }

    if (status === 400) {
      return {
        data: {
          ...error.response.data,
          message: error.response.data?.message || "Bad Request",
        },
        status: 400,
        statusText: "Bad Request",
        headers: error.response.headers,
        config: error.config,
      };
    }

    return Promise.reject(error);
  }
);


function apiGet(url, params = {}) {
  return instance.get(url, { params });
}

function apiGet2(url, params = {}) {
  return instance2.get(url, { params });
}

function apiPost(url, body) {
  return instance.post(url, body);
}

function apiPut(url, body) {
  return instance.put(url, body);
}

function apiDelete(url) {
  return instance.delete(url);
}

export { getHeaders, apiGet, apiGet2, apiPost, apiPut, apiDelete };
